import React, { lazy } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
} from 'react-router-dom';
import NavbarHeader from '../shared/Navbar/Navbar';
import { PrivateRoute } from '../auth/PrivateRoute';
import { ROLES, ROUTES } from '../lib/constants';

const Home = lazy(() => import('../pages/Home/Home'));
const Users = lazy(() => import('../pages/users'));
const Intelligence = lazy(() => import('../pages/intelligence'));
const Candidates = lazy(() => import('../pages/candidates'));
const Editor = lazy(() => import('../pages/editor'));
const WelcomePage = lazy(() => import('../pages/challenge-welcome-page'));
const ChallengeSubmitted = lazy(() => import('../pages/challenge-submitted-page'));
const ContestSubmitted = lazy(() => import('../pages/contest-submitted-page'));
const Login = lazy(() => import('../pages/login'));
const CandidateExperience = lazy(() => import('../pages/candidate-experience'));
const Result = lazy(() => import('../pages/result'));
const Interviews = lazy(() => import('../pages/interviews'));
const AboutUs = lazy(() => import('../pages/about-us'));
const Languages = lazy(() => import('../pages/languages'));
const TestingComponents = lazy(() => import('../pages/testing-components'));
const Contests = lazy(() => import('../pages/contests'));
const ContestDetail = lazy(() => import('../pages/contest-detail'));
const ContestSubmissions = lazy(() => import('../pages/contest-submissions'));
const ContestResultPage = lazy(() => import('../pages/contest-result-page'));
const ContestResultsPage = lazy(() => import('../pages/contest-results-page'));
const Training = lazy(() => import('../pages/training'));
const Hackathons = lazy(() => import('../pages/hackathons'));
const HackathonDetail = lazy(() => import('../pages/hackathon-detail'));
const HackathonSubmissions = lazy(() => import('../pages/hackathon-submissions'));
const HackathonResults = lazy(() => import('../pages/hackathon-results'));

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route path="/">
      {/* Public URLs */}
      <Route path={ROUTES.LOGIN} element={<Login />} />
      <Route path={ROUTES.EDITOR} element={<Editor />} />
      <Route path={ROUTES.WELCOME_CHALLENGE} element={<WelcomePage />} />
      <Route
        path={ROUTES.SUBMITTED_CHALLENGE}
        element={<ChallengeSubmitted />}
      />
      <Route
        path={ROUTES.SUBMITTED_CONTEST}
        element={<ContestSubmitted />}
      />
      <Route
        path={ROUTES.CANDIDATE_EXPERIENCE}
        element={<CandidateExperience />}
      />
      <Route
        path={ROUTES.HACKATHON_RESULTS}
        element={
          <PrivateRoute allowedRoles={[ROLES.ADMIN, ROLES.USER]}>
            <HackathonResults />
          </PrivateRoute>
        }
      />

      <Route path="/" element={<NavbarHeader />}>
        {/* Protected URLs */}
        <Route
          path={ROUTES.HOME}
          element={
            <PrivateRoute allowedRoles={[ROLES.ADMIN, ROLES.TA, ROLES.INTERVIEWER, ROLES.USER]}>
              <Home />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.ABOUT}
          element={
            <PrivateRoute allowedRoles={[ROLES.ADMIN, ROLES.TA, ROLES.INTERVIEWER, ROLES.USER]}>
              <AboutUs />
            </PrivateRoute>
          }
        />

        {/* Role Based Protected URLs */}
        <Route
          path={ROUTES.USERS}
          element={
            <PrivateRoute allowedRoles={[ROLES.ADMIN]}>
              <Users />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.LANGUAGES}
          element={
            <PrivateRoute allowedRoles={[ROLES.ADMIN]}>
              <Languages />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.CONTESTS}
          element={
            <PrivateRoute allowedRoles={[ROLES.ADMIN, ROLES.TA, ROLES.INTERVIEWER, ROLES.USER]}>
              <Contests />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.CONTEST_DETAIL}
          element={
            <PrivateRoute allowedRoles={[ROLES.ADMIN, ROLES.TA, ROLES.INTERVIEWER, ROLES.USER]}>
              <ContestDetail />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.CONTEST_SUBMISSIONS}
          element={
            <PrivateRoute allowedRoles={[ROLES.ADMIN]}>
              <ContestSubmissions />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.CONTEST_RESULT_DETAIL}
          element={
            <PrivateRoute allowedRoles={[ROLES.ADMIN]}>
              <ContestResultPage />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.CONTEST_RESULTS}
          element={
            <PrivateRoute allowedRoles={[ROLES.ADMIN, ROLES.TA, ROLES.INTERVIEWER, ROLES.USER]}>
              <ContestResultsPage />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.TRAINING}
          element={
            <PrivateRoute allowedRoles={[ROLES.ADMIN, ROLES.USER]}>
              <Training />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.HACKATHONS}
          element={
            <PrivateRoute allowedRoles={[ROLES.ADMIN, ROLES.USER]}>
              <Hackathons />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.HACKATHON_DETAIL}
          element={
            <PrivateRoute allowedRoles={[ROLES.ADMIN, ROLES.USER]}>
              <HackathonDetail />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.HACKATHON_SUBMISSIONS}
          element={
            <PrivateRoute allowedRoles={[ROLES.ADMIN]}>
              <HackathonSubmissions />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.INTELLIGENCE}
          element={
            <PrivateRoute allowedRoles={[ROLES.ADMIN, ROLES.TA, ROLES.INTERVIEWER]}>
              <Intelligence />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.CANDIDATES}
          element={
            <PrivateRoute allowedRoles={[ROLES.ADMIN, ROLES.TA]}>
              <Candidates />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.INTERVIEWS}
          element={
            <PrivateRoute
              allowedRoles={[ROLES.ADMIN, ROLES.TA, ROLES.INTERVIEWER]}
            >
              <Interviews />
            </PrivateRoute>
          }
        />
        <Route
          path={ROUTES.RESULT}
          element={
            <PrivateRoute
              allowedRoles={[ROLES.ADMIN, ROLES.TA, ROLES.INTERVIEWER, ROLES.USER]}
            >
              <Result />
            </PrivateRoute>
          }
        />
        <Route path={"/testing-components"} element={<TestingComponents />} />
        <Route
          path="*"
          element={
            <PrivateRoute>
              <Home />
            </PrivateRoute>
          }
        />
      </Route>
    </Route>
  )
);

export default router;
